<template>
  <form class="contact-form" novalidate @submit.prevent="submitForm">
    <div v-if="successMessage" ref="feedbackRef" class="contact-form__alert contact-form__alert--success">
      {{ successMessage }}
    </div>

    <div v-if="errorMessage" ref="feedbackRef" class="contact-form__alert contact-form__alert--error">
      {{ errorMessage }}
    </div>

    <div v-if="!successMessage" class="contact-form__grid">
      <div class="contact-form__field">
        <label class="contact-form__label" for="contact-name">
          Name <span class="contact-form__required" aria-hidden="true">*</span>
        </label>
        <input
          id="contact-name"
          ref="field-name"
          type="text"
          class="contact-form__input"
          :class="{ 'contact-form__input--invalid': showFieldError('name') }"
          placeholder="Ihr Name"
          v-model="form.name"
          autocomplete="name"
          :aria-invalid="showFieldError('name') ? 'true' : 'false'"
          aria-describedby="contact-name-error"
          @blur="touchField('name')"
        />
        <p
          v-if="showFieldError('name')"
          id="contact-name-error"
          class="contact-form__field-error"
          role="alert"
        >
          {{ fieldErrors.name }}
        </p>
      </div>

      <div class="contact-form__field">
        <label class="contact-form__label" for="contact-email">
          E-Mail <span class="contact-form__required" aria-hidden="true">*</span>
        </label>
        <input
          id="contact-email"
          ref="field-email"
          type="email"
          class="contact-form__input"
          :class="{ 'contact-form__input--invalid': showFieldError('email') }"
          placeholder="E-Mail-Adresse"
          v-model="form.email"
          autocomplete="email"
          inputmode="email"
          :aria-invalid="showFieldError('email') ? 'true' : 'false'"
          aria-describedby="contact-email-error"
          @blur="touchField('email')"
        />
        <p
          v-if="showFieldError('email')"
          id="contact-email-error"
          class="contact-form__field-error"
          role="alert"
        >
          {{ fieldErrors.email }}
        </p>
      </div>

      <div class="contact-form__field">
        <label class="contact-form__label" for="contact-phone">
          Telefon
          <span class="contact-form__optional">optional</span>
        </label>
        <input
          id="contact-phone"
          type="tel"
          class="contact-form__input"
          placeholder="Telefon (optional)"
          v-model="form.phone"
          autocomplete="tel"
        />
      </div>

      <div class="contact-form__field">
        <label class="contact-form__label" for="contact-subject">
          Betreff <span class="contact-form__required" aria-hidden="true">*</span>
        </label>
        <input
          id="contact-subject"
          ref="field-subject"
          type="text"
          class="contact-form__input"
          :class="{ 'contact-form__input--invalid': showFieldError('subject') }"
          placeholder="Betreff"
          v-model="form.subject"
          :aria-invalid="showFieldError('subject') ? 'true' : 'false'"
          aria-describedby="contact-subject-error"
          @blur="touchField('subject')"
        />
        <p
          v-if="showFieldError('subject')"
          id="contact-subject-error"
          class="contact-form__field-error"
          role="alert"
        >
          {{ fieldErrors.subject }}
        </p>
      </div>

      <div class="contact-form__field contact-form__field--full">
        <label class="contact-form__label" for="contact-message">
          Nachricht <span class="contact-form__required" aria-hidden="true">*</span>
        </label>
        <textarea
          id="contact-message"
          ref="field-message"
          class="contact-form__input contact-form__textarea"
          :class="{ 'contact-form__input--invalid': showFieldError('message') }"
          placeholder="Ihre Nachricht"
          v-model="form.message"
          rows="5"
          :aria-invalid="showFieldError('message') ? 'true' : 'false'"
          aria-describedby="contact-message-error"
          @blur="touchField('message')"
        ></textarea>
        <p
          v-if="showFieldError('message')"
          id="contact-message-error"
          class="contact-form__field-error"
          role="alert"
        >
          {{ fieldErrors.message }}
        </p>
      </div>

      <div class="contact-form__field contact-form__field--full">
        <label class="contact-form__label" for="contact-captcha">
          Sicherheitsprüfung
        </label>
        <div id="contact-captcha" ref="captchaSection" class="contact-form__captcha">
          <FriendlyCaptchaField
            :key="captchaKey"
            @validated="captchaValid = $event"
            @error="onCaptchaError"
          />
        </div>
        <p
          v-if="showCaptchaHint"
          class="contact-form__hint contact-form__hint--error"
          role="alert"
        >
          Bitte lösen Sie die Sicherheitsprüfung, bevor Sie absenden.
        </p>
        <button
          type="submit"
          class="contact-form__submit button white"
          :disabled="submitDisabled"
        >
          {{ submitting ? 'Wird gesendet…' : 'Absenden' }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { useApi } from '../../composables/useApi.js'
import { useContent } from '../../composables/useContent.js'
import { useCookieConsent } from '../../composables/useCookieConsent.js'
import { isContactFormValid, validateContactForm } from '../../lib/contactForm.js'
import { trackContactSubmit } from '../../lib/analytixTracking.js'
import FriendlyCaptchaField from './FriendlyCaptchaField.vue'

const VALIDATED_FIELDS = ['name', 'email', 'subject', 'message']

export default {
  components: {
    FriendlyCaptchaField,
  },
  setup() {
    const api = useApi()
    const content = useContent()
    const { externalAllowed } = useCookieConsent()

    return {
      api,
      content,
      externalAllowed,
    }
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      },
      touched: {},
      submitted: false,
      captchaValid: false,
      captchaKey: 0,
      submitting: false,
      successMessage: '',
      errorMessage: ''
    }
  },
  computed: {
    fieldErrors() {
      return validateContactForm(this.form)
    },
    submitDisabled() {
      return this.submitting || !this.externalAllowed
    },
    showCaptchaHint() {
      return this.submitted && this.externalAllowed && !this.captchaValid
    },
  },
  methods: {
    touchField(field) {
      this.touched = { ...this.touched, [field]: true }
    },
    showFieldError(field) {
      return Boolean(this.fieldErrors[field] && (this.submitted || this.touched[field]))
    },
    resetValidation() {
      this.touched = {}
      this.submitted = false
    },
    focusFirstInvalidField() {
      const firstInvalid = VALIDATED_FIELDS.find((field) => this.fieldErrors[field])
      if (!firstInvalid) return

      const ref = this.$refs[`field-${firstInvalid}`]
      const element = Array.isArray(ref) ? ref[0] : ref
      element?.focus?.()
      element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    },
    resetCaptcha() {
      this.captchaValid = false
      this.captchaKey += 1
    },
    onCaptchaError(message) {
      this.errorMessage = message
    },
    async submitForm() {
      this.submitted = true
      this.errorMessage = ''

      if (!isContactFormValid(this.form)) {
        this.$nextTick(() => this.focusFirstInvalidField())
        return
      }

      if (!this.externalAllowed) {
        return
      }

      if (!this.captchaValid) {
        this.$nextTick(() => {
          this.$refs.captchaSection?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
        })
        return
      }

      if (this.submitting) return

      this.submitting = true

      try {
        if (!this.content.hasContact.value) {
          await this.content.loadContact()
        }

        const mailTo =
          this.content.contact.value?.email || 'info@fewo-in-ahrenshoop.de'

        await this.api.sendContactMessage(mailTo, { ...this.form })
        trackContactSubmit({ subject: this.form.subject })

        this.successMessage =
          'Vielen Dank für Ihre Nachricht — wir melden uns in Kürze bei Ihnen.'
        this.form = {
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        }
        this.resetValidation()
        this.resetCaptcha()
        this.$nextTick(() => {
          this.$refs.feedbackRef?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
        })
      } catch (error) {
        console.error('Kontaktformular Fehler:', error)
        this.errorMessage =
          error?.message ||
          'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
        this.resetCaptcha()
      } finally {
        this.submitting = false
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.contact-form {
  margin-top: 1.75rem;
}

.contact-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.contact-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;

  &--full {
    grid-column: 1 / -1;
  }
}

.contact-form__label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.contact-form__required {
  color: #42d9e4;
  font-weight: 700;
}

.contact-form__optional {
  margin-left: 0.35rem;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: none;
  color: rgba(255, 255, 255, 0.45);
}

.contact-form__field-error {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #ffc9c9;
}

.contact-form__hint {
  margin: 0 0 0.65rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.62);

  &--error {
    color: #ffc9c9;
  }
}

.contact-form__input {
  width: 100%;
  min-height: 48px;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  color: #2f363a;
  font: inherit;
  font-size: 1rem;
  line-height: 1.4;
  box-shadow: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    color: rgba(81, 90, 95, 0.55);
  }

  &:focus {
    outline: none;
    border-color: #42d9e4;
    box-shadow: 0 0 0 3px rgba(66, 217, 228, 0.22);
  }

  &--invalid {
    border-color: #e57373;
    background: #fffafa;

    &:focus {
      border-color: #e57373;
      box-shadow: 0 0 0 3px rgba(229, 115, 115, 0.24);
    }
  }
}

.contact-form__textarea {
  min-height: 140px;
  resize: vertical;
}

.contact-form__captcha {
  margin-bottom: 0.75rem;

  :deep(.frc-captcha) {
    width: 100%;
    max-width: none;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    color: #2f363a;
    overflow: hidden;
  }

  :deep(.frc-captcha *) {
    color: #515a5f;
  }

  :deep(.frc-container) {
    min-height: 48px;
    padding: 0 0.35rem;
  }

  :deep(.frc-icon) {
    fill: #42d9e4;
    stroke: #42d9e4;
    margin: 0 0.5rem 0 0.65rem;
  }

  :deep(.frc-content) {
    margin: 0;
    padding: 0.65rem 0.75rem 0.65rem 0;
  }

  :deep(.frc-text) {
    font-size: 0.95rem;
    font-weight: 500;
    color: #2f363a;
  }

  :deep(.frc-button) {
    margin-top: 0.35rem;
    border-radius: 6px;
    background: #f3f5f6;
    border-color: #dde2e5;
    color: #2f363a;
    font-size: 0.875rem;

    &:hover {
      background: #e8ecef;
    }
  }

  :deep(.frc-progress) {
    margin-top: 0.35rem;
    border-radius: 999px;
    background-color: #e8ecef;
    color: #42d9e4;
  }

  :deep(.frc-progress::-webkit-progress-value),
  :deep(.frc-progress::-moz-progress-bar) {
    background: #42d9e4;
  }
}

.contact-form__submit {
  width: 100%;
  min-height: 48px;
  margin-top: 0.25rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.contact-form__alert {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  line-height: 1.5;
}

.contact-form__alert--success {
  background: #edf7ed;
  border: 1px solid #b8dfb9;
  color: #2f6b2f !important;
  font-weight: 600;
}

.contact-form__alert--error {
  background: #fdecec;
  border: 1px solid #f0c4c4;
  color: #8f4d4d !important;
}

@media (max-width: 767px) {
  .contact-form {
    margin-top: 1.35rem;
  }

  .contact-form__grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .contact-form__input {
    font-size: 1.0625rem;
  }
}
</style>
